// Secure token storage utility
// Implements best practices for token management in React applications

class SecureTokenStorage {
  private static instance: SecureTokenStorage;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  private constructor() {
    // Initialize from localStorage (shared across tabs)
    this.loadFromStorage();
  }

  static getInstance(): SecureTokenStorage {
    if (!SecureTokenStorage.instance) {
      SecureTokenStorage.instance = new SecureTokenStorage();
    }
    return SecureTokenStorage.instance;
  }

  // Store access token securely
  setAccessToken(accessToken: string): void {
    this.accessToken = accessToken;

    // Store in localStorage to share across tabs (same origin)
    // Note: This allows tokens to be available when opening URLs in new tabs
    try {
      localStorage.setItem('auth_token', accessToken);
    } catch {
      // Failed to store token
    }
  }

  // Store refresh token securely
  setRefreshToken(refreshToken: string): void {
    this.refreshToken = refreshToken;

    // Store in localStorage to share across tabs (same origin)
    try {
      localStorage.setItem('refresh_token', refreshToken);
    } catch {
      // Failed to store token
    }
  }

  // Get access token from memory, with fallback to localStorage
  getAccessToken(): string | null {
    // If token is in memory, return it
    if (this.accessToken) {
      return this.accessToken;
    }

    // Fallback to localStorage (in case singleton was created before token was set)
    try {
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        this.accessToken = storedToken;
        return storedToken;
      }
    } catch {
      // Failed to read from localStorage
    }

    return null;
  }

  // Get refresh token from memory, with fallback to localStorage
  getRefreshToken(): string | null {
    // If token is in memory, return it
    if (this.refreshToken) {
      return this.refreshToken;
    }

    // Fallback to localStorage (in case singleton was created before token was set)
    try {
      const storedToken = localStorage.getItem('refresh_token');
      if (storedToken) {
        this.refreshToken = storedToken;
        return storedToken;
      }
    } catch {
      // Failed to read from localStorage
    }

    return null;
  }

  // Clear access token
  clearAccessToken(): void {
    this.accessToken = null;

    // Clear from localStorage
    try {
      localStorage.removeItem('auth_token');
    } catch {
      // Failed to clear token
    }
  }

  // Clear refresh token
  clearRefreshToken(): void {
    this.refreshToken = null;

    // Clear from localStorage
    try {
      localStorage.removeItem('refresh_token');
    } catch {
      // Failed to clear token
    }
  }

  // Clear all tokens
  clearAllTokens(): void {
    this.clearAccessToken();
    this.clearRefreshToken();
  }

  // Load token from localStorage (shared across tabs)
  private loadFromStorage(): void {
    try {
      const storedAccessToken = localStorage.getItem('auth_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');

      if (storedAccessToken) {
        this.accessToken = storedAccessToken;
      }

      if (storedRefreshToken) {
        this.refreshToken = storedRefreshToken;
      }
    } catch {
      // Failed to load token
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
    }
  }
}

// Export singleton instance
export const tokenStorage = SecureTokenStorage.getInstance();
